import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactDataGrid, { Column, renderSortIcon, renderSortPriority, SortColumn } from "react-data-grid";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { getPLAHierarchicalRelations, PLAElementRecord, PLAElementTypeDefinition, PLAProject, PLARelationRecord } from "./PLAState";
import { getSortedPLAAttributeEntries } from "./PLAAttributeDefinitions";
import { getPLAAlertDetails, resolvePLACalculatedAttributes } from "./PLAExpression";
import { formatPLAAttributeValue } from "./PLAFormatting";
import { PLAAlertMark } from "./PLAAlertMark";
import "./PLATable.css";

export type PLATableRef = {
   scrollToRow: (elementId: string, focusRow?: boolean) => void;
};

type PLATableRow = PLAElementRecord & {
   __rowKey: string;
   __alerts: string[];
   __alertsByAttributeId: Record<string, string[]>;
   __calculated: Record<string, any>;
   __depth: number;
   __expanded: boolean;
   __hasChildren: boolean;
};

type PLATablePreferences = {
   visibleColumns?: Record<string, boolean>;
   sortColumns?: readonly SortColumn[];
   columnWidths?: Record<string, number>;
};

type Props = {
   project: PLAProject;
   elementTypeId: string;
   elementType: PLAElementTypeDefinition;
   elements: PLAElementRecord[];
   allElements: PLAElementRecord[];
   relations: PLARelationRecord[];
   selectedElementId?: string;
   readOnly: boolean;
   onRowClick: (element: PLAElementRecord) => void;
   onAddElement: () => void;
   onOpenElement: (element: PLAElementRecord) => void;
   onCreateRelation: (element: PLAElementRecord) => void;
   onDeleteElement: (element: PLAElementRecord) => void;
};

function compareValues(a: any, b: any) {
   if (a === b) return 0;
   if (a === undefined || a === null) return -1;
   if (b === undefined || b === null) return 1;
   if (typeof a === "number" && typeof b === "number") return a - b;
   return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function getPLAElementNameSectionSortKey(name: string) {
   const match = name.match(/^(.*?)(\d+)(?:\.(\d+))?(.*)$/);
   if (!match) return undefined;
   return {
      prefix: match[1].trim(),
      section: Number.parseInt(match[2], 10),
      subsection: match[3] === undefined ? 0 : Number.parseInt(match[3], 10),
      hasSubsection: match[3] !== undefined,
      suffix: match[4].trim()
   };
}

function comparePLAElementNames(leftName: string, rightName: string) {
   const leftKey = getPLAElementNameSectionSortKey(leftName);
   const rightKey = getPLAElementNameSectionSortKey(rightName);
   if (leftKey && rightKey) {
      if (leftKey.section !== rightKey.section) return leftKey.section - rightKey.section;
      if (!leftKey.hasSubsection && rightKey.hasSubsection) return -1;
      if (leftKey.hasSubsection && !rightKey.hasSubsection) return 1;
      if (leftKey.subsection !== rightKey.subsection) return leftKey.subsection - rightKey.subsection;
      const prefixResult = compareValues(leftKey.prefix, rightKey.prefix);
      if (prefixResult !== 0) return prefixResult;
      const suffixResult = compareValues(leftKey.suffix, rightKey.suffix);
      if (suffixResult !== 0) return suffixResult;
   }
   return compareValues(leftName, rightName);
}

function hasPLAElementSortOrder(element: PLAElementRecord) {
   return typeof element.sortingNumber === "number" && Number.isFinite(element.sortingNumber);
}

function comparePLAElementSortOrder(leftElement: PLAElementRecord, rightElement: PLAElementRecord) {
   const leftHasSortOrder = hasPLAElementSortOrder(leftElement);
   const rightHasSortOrder = hasPLAElementSortOrder(rightElement);
   if (leftHasSortOrder && rightHasSortOrder && leftElement.sortingNumber !== rightElement.sortingNumber) return leftElement.sortingNumber! - rightElement.sortingNumber!;
   if (leftHasSortOrder && !rightHasSortOrder) return -1;
   if (!leftHasSortOrder && rightHasSortOrder) return 1;
   return 0;
}

function comparePLAElementOrder(leftElement: PLAElementRecord, rightElement: PLAElementRecord) {
   const sortOrderResult = comparePLAElementSortOrder(leftElement, rightElement);
   if (sortOrderResult !== 0) return sortOrderResult;
   return comparePLAElementNames(leftElement.name, rightElement.name);
}

function getAttributeDisplay(row: PLATableRow, attributeId: string) {
   if (Object.prototype.hasOwnProperty.call(row.__calculated, attributeId)) return row.__calculated[attributeId];
   const value = row.attributes[attributeId];
   return value?.mode === "constant" ? value.value : value?.expression || "";
}

function sortPLATableRows(rows: PLATableRow[], sortColumns: readonly SortColumn[]) {
   if (sortColumns.length === 0) return [...rows].sort(comparePLAElementOrder);
   return [...rows].sort((leftRow, rightRow) => {
      const sortOrderResult = comparePLAElementSortOrder(leftRow, rightRow);
      if (sortOrderResult !== 0) return sortOrderResult;
      for (const sort of sortColumns) {
         const leftValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(leftRow, sort.columnKey.slice(5)) : (leftRow as any)[sort.columnKey];
         const rightValue = sort.columnKey.startsWith("attr:") ? getAttributeDisplay(rightRow, sort.columnKey.slice(5)) : (rightRow as any)[sort.columnKey];
         const result = sort.columnKey === "name" ? comparePLAElementNames(String(leftValue || ""), String(rightValue || "")) : compareValues(leftValue, rightValue);
         if (result !== 0) return sort.direction === "ASC" ? result : -result;
      }
      return 0;
   });
}

function sortPLAChildElementsByName(elements: PLAElementRecord[]) {
   return [...elements].sort(comparePLAElementOrder);
}

function RowActionButton(props: { title: string; icon: "edit" | "mapLink" | "delete"; onClick: () => void }) {
   return (
      <GlassButton
         className="pla-row-action"
         title={props.title}
         aria-label={props.title}
         onClick={(event) => {
            event.stopPropagation();
            props.onClick();
         }}
      >
         <MIBIcon icon={props.icon} />
      </GlassButton>
   );
}

function RowExpandButton(props: { expanded: boolean; onClick: () => void }) {
   const title = Localization.getText(props.expanded ? "Collapse children" : "Expand children");
   return (
      <button
         type="button"
         className="pla-row-expand"
         title={title}
         aria-label={title}
         aria-expanded={props.expanded}
         onClick={(event) => {
            event.stopPropagation();
            props.onClick();
         }}
      >
         <MIBIcon icon={props.expanded ? "expandable" : "collapsable"} />
      </button>
   );
}

function PLAAttributeHeader(props: { name: string; description?: string; sortDirection?: "ASC" | "DESC"; priority?: number; sortable?: boolean }) {
   const description = props.description?.trim();
   return (
      <span className="pla-attribute-header" title={description || undefined}>
         <span className={description ? "pla-attribute-header__name pla-attribute-header__name--has-description" : "pla-attribute-header__name"}>{props.name}</span>
         {props.sortable && (
            <span className="pla-attribute-header__sort">
               {renderSortIcon({ sortDirection: props.sortDirection })}
               {renderSortPriority({ priority: props.priority })}
            </span>
         )}
      </span>
   );
}

function initialVisibleColumns(elementType: PLAElementTypeDefinition): Record<string, boolean> {
   const visibleColumns: Record<string, boolean> = { name: true, sortingNumber: false };
   getSortedPLAAttributeEntries(elementType.attributes).forEach(([attributeId, definition]) => {
      if (isElementNameAttribute(attributeId, definition.name)) return;
      visibleColumns[`attr:${attributeId}`] = !definition.hideAsDefault;
   });
   return visibleColumns;
}

function getColumnOptions(elementType: PLAElementTypeDefinition) {
   return [
      { key: "name", name: Localization.getText("Name") },
      { key: "sortingNumber", name: Localization.getText("Sorting number") },
      ...getSortedPLAAttributeEntries(elementType.attributes)
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({ key: `attr:${attributeId}`, name: definition.name || attributeId }))
   ];
}

function getPLATablePreferencesKey(userId: string, projectId: string, elementTypeId: string) {
   return `PLAState-tablePreferences-${userId}-${projectId}-${elementTypeId}`;
}

function readPLATablePreferences(key: string): PLATablePreferences {
   try {
      return JSON.parse(localStorage.getItem(key) || "{}") || {};
   } catch {
      return {};
   }
}

function normalizeVisibleColumns(elementType: PLAElementTypeDefinition, stored?: Record<string, boolean>): Record<string, boolean> {
   const defaults = initialVisibleColumns(elementType);
   const validKeys = new Set(Object.keys(defaults));
   return Object.entries(defaults).reduce<Record<string, boolean>>((result, [key, defaultValue]) => {
      result[key] = validKeys.has(key) && typeof stored?.[key] === "boolean" ? stored[key] : defaultValue;
      return result;
   }, {});
}

function normalizeSortColumns(elementType: PLAElementTypeDefinition, stored?: readonly SortColumn[]): SortColumn[] {
   if (!Array.isArray(stored)) return [];
   const validKeys = new Set(getColumnOptions(elementType).map((column) => column.key));
   return stored.filter((sort) => validKeys.has(sort.columnKey) && (sort.direction === "ASC" || sort.direction === "DESC"));
}

function normalizeColumnWidths(elementType: PLAElementTypeDefinition, stored?: Record<string, number>): Record<string, number> {
   if (!stored || typeof stored !== "object") return {};
   const validKeys = new Set(getColumnOptions(elementType).map((column) => column.key));
   return Object.entries(stored).reduce<Record<string, number>>((result, [key, width]) => {
      if (validKeys.has(key) && typeof width === "number" && Number.isFinite(width) && width > 0) result[key] = width;
      return result;
   }, {});
}

function isElementNameAttribute(attributeId: string, attributeName?: string) {
   const reservedNames = new Set(["name", Localization.getText("Name").trim().toLowerCase()]);
   return reservedNames.has(attributeId.trim().toLowerCase()) || reservedNames.has((attributeName || "").trim().toLowerCase());
}

export const PLATable = forwardRef<PLATableRef, Props>((props, ref) => {
   const { state: sessionState } = useContext(SessionContext);
   const containerRef = useRef<HTMLDivElement | null>(null);
   const gridRef = useRef<any>(null);
   const skipNextPreferencesPersist = useRef(false);
   const storageUserId = sessionState.userName || sessionState.userRef || "anonymous";
   const preferencesKey = useMemo(() => getPLATablePreferencesKey(storageUserId, props.project.id, props.elementTypeId), [storageUserId, props.project.id, props.elementTypeId]);
   const [filter, setFilter] = useState("");
   const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>(() => normalizeSortColumns(props.elementType, readPLATablePreferences(preferencesKey).sortColumns));
   const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => normalizeVisibleColumns(props.elementType, readPLATablePreferences(preferencesKey).visibleColumns));
   const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => normalizeColumnWidths(props.elementType, readPLATablePreferences(preferencesKey).columnWidths));
   const [expandedElementIds, setExpandedElementIds] = useState<Set<string>>(() => new Set());

   useEffect(() => {
      const stored = readPLATablePreferences(preferencesKey);
      skipNextPreferencesPersist.current = true;
      setVisibleColumns(normalizeVisibleColumns(props.elementType, stored.visibleColumns));
      setSortColumns(normalizeSortColumns(props.elementType, stored.sortColumns));
      setColumnWidths(normalizeColumnWidths(props.elementType, stored.columnWidths));
   }, [preferencesKey, props.elementType]);

   useEffect(() => {
      if (skipNextPreferencesPersist.current) {
         skipNextPreferencesPersist.current = false;
         return;
      }
      localStorage.setItem(preferencesKey, JSON.stringify({ visibleColumns, sortColumns, columnWidths } satisfies PLATablePreferences));
   }, [preferencesKey, sortColumns, visibleColumns, columnWidths]);

   const childIdsByElementId = useMemo(() => {
      const elementIds = new Set(props.allElements.map((element) => element.id));
      return getPLAHierarchicalRelations(props.project, props.relations).reduce<Map<string, string[]>>((children, relation) => {
         if (!elementIds.has(relation.fromId) || !elementIds.has(relation.toId) || relation.fromId === relation.toId) return children;
         children.set(relation.fromId, [...(children.get(relation.fromId) || []), relation.toId]);
         return children;
      }, new Map());
   }, [props.project, props.relations, props.allElements]);

   useEffect(() => {
      setExpandedElementIds((previous) => {
         const next = new Set([...previous].filter((elementId) => childIdsByElementId.has(elementId)));
         return next.size === previous.size ? previous : next;
      });
   }, [childIdsByElementId]);

   const toggleExpandedElement = useCallback((elementId: string) => {
      setExpandedElementIds((previous) => {
         const next = new Set(previous);
         if (next.has(elementId)) next.delete(elementId);
         else next.add(elementId);
         return next;
      });
   }, []);

   const elementsById = useMemo(() => new Map(props.allElements.map((element) => [element.id, element])), [props.allElements]);

   const createTableRow = useCallback((element: PLAElementRecord, depth = 0, rowKey = element.id): PLATableRow => {
      let calculated: Record<string, any> = {};
      let alerts: string[] = [];
      let alertsByAttributeId: Record<string, string[]> = {};
      try {
         calculated = resolvePLACalculatedAttributes(element, props.project, props.allElements, props.relations, { includeErrors: true });
         const alertDetails = getPLAAlertDetails(element, props.project, props.allElements, props.relations);
         alerts = alertDetails.map((alert) => alert.message);
         alertsByAttributeId = alertDetails.reduce<Record<string, string[]>>((acc, alert) => {
            acc[alert.attributeId] = [...(acc[alert.attributeId] || []), alert.message];
            return acc;
         }, {});
      } catch (error: any) {
         alerts = [error.message || String(error)];
      }
      return { ...element, __rowKey: rowKey, __alerts: alerts, __alertsByAttributeId: alertsByAttributeId, __calculated: calculated, __depth: depth, __expanded: expandedElementIds.has(element.id), __hasChildren: (childIdsByElementId.get(element.id) || []).length > 0 };
   }, [props.project, props.allElements, props.relations, expandedElementIds, childIdsByElementId]);

   const rows: PLATableRow[] = useMemo(() => props.elements.map((element) => createTableRow(element)), [props.elements, createTableRow]);

   const filteredRows = useMemo(() => {
      const lowerFilter = filter.trim().toLowerCase();
      const visible = lowerFilter
         ? rows.filter((row) => JSON.stringify({ name: row.name, attributes: row.attributes, calculated: row.__calculated }).toLowerCase().includes(lowerFilter))
         : rows;
      const flattenedRows: PLATableRow[] = [];

      function appendExpandedChildren(parentRow: PLATableRow, depth: number, visitedElementIds: Set<string>) {
         if (!parentRow.__expanded) return;
         const childRows = sortPLAChildElementsByName((childIdsByElementId.get(parentRow.id) || [])
            .filter((childId) => !visitedElementIds.has(childId))
            .map((childId) => elementsById.get(childId))
            .filter((element): element is PLAElementRecord => Boolean(element)))
            .map((childElement) => createTableRow(childElement, depth, `${parentRow.__rowKey}/${childElement.id}`));
         childRows.forEach((nestedRow) => {
            flattenedRows.push(nestedRow);
            appendExpandedChildren(nestedRow, depth + 1, new Set([...visitedElementIds, nestedRow.id]));
         });
      }

      sortPLATableRows(visible, sortColumns).forEach((row) => {
         const topRow = { ...row, __depth: 0, __expanded: expandedElementIds.has(row.id) };
         flattenedRows.push(topRow);
         appendExpandedChildren(topRow, 1, new Set([row.id]));
      });

      return flattenedRows;
   }, [rows, elementsById, childIdsByElementId, expandedElementIds, createTableRow, filter, sortColumns]);

   const focusSelectedRow = useCallback(() => {
      window.setTimeout(() => {
         const selectedCell = containerRef.current?.querySelector<HTMLElement>(".rdg-row-selected .rdg-cell");
         if (!selectedCell) return;
         selectedCell.tabIndex = -1;
         selectedCell.focus();
      }, 0);
   }, []);

   const scrollToRow = useCallback((elementId: string, focusRow = false) => {
      const rowIndex = filteredRows.findIndex((row) => row.id === elementId);
      if (rowIndex >= 0) {
         gridRef.current?.scrollToCell?.({ rowIdx: rowIndex, idx: 0 });
         if (focusRow) focusSelectedRow();
      }
   }, [filteredRows, focusSelectedRow]);

   useImperativeHandle(ref, () => ({ scrollToRow }), [scrollToRow]);

   useEffect(() => {
      if (props.selectedElementId) scrollToRow(props.selectedElementId, true);
   }, [props.selectedElementId, scrollToRow]);

   const setColumnVisible = useCallback((columnKey: string, visible: boolean) => {
      setVisibleColumns((prev) => ({ ...prev, [columnKey]: visible }));
      if (!visible) {
         setColumnWidths((prev) => {
            if (prev[columnKey] === undefined) return prev;
            const next = { ...prev };
            delete next[columnKey];
            return next;
         });
      }
   }, []);

   const columns: Column<PLATableRow>[] = useMemo(() => {
      const attributeColumns: Column<PLATableRow>[] = getSortedPLAAttributeEntries(props.elementType.attributes)
         .filter(([attributeId, definition]) => !isElementNameAttribute(attributeId, definition.name))
         .map(([attributeId, definition]) => ({
            key: `attr:${attributeId}`,
            name: definition.name || attributeId,
            width: columnWidths[`attr:${attributeId}`],
            sortable: true,
            resizable: true,
            renderHeaderCell: ({ column, sortDirection, priority }) => (
               <PLAAttributeHeader
                  name={definition.name || attributeId}
                  description={definition.description}
                  sortDirection={sortDirection}
                  priority={priority}
                  sortable={column.sortable}
               />
            ),
            renderCell: ({ row }) => {
               const alertMessages = row.__alertsByAttributeId[attributeId] || [];
               return <span title={alertMessages.join("\n") || undefined}>{formatPLAAttributeValue(getAttributeDisplay(row, attributeId), definition)}</span>;
            },
            cellClass: (row) => (row.__alertsByAttributeId[attributeId] || []).length ? "pla-alert-cell" : undefined
         }));
      return [
         {
            key: "expand",
            name: "",
            width: 42,
            renderCell: ({ row }) => row.__hasChildren ? <RowExpandButton expanded={row.__expanded} onClick={() => toggleExpandedElement(row.id)} /> : null,
            cellClass: (row) => row.__depth > 0 ? "pla-tree-depth-cell" : undefined
         },
         {
            key: "alert",
            name: "",
            width: 42,
            renderCell: ({ row }) => row.__alerts.length ? <PLAAlertMark messages={row.__alerts} placement="right" /> : null
         },
         ...(visibleColumns.name ? [{
            key: "name",
            name: Localization.getText("Name"),
            width: columnWidths.name,
            sortable: true,
            resizable: true,
            colSpan: (args) => args.type === "ROW" && args.row.__depth > 0 ? 3 : undefined,
            renderCell: ({ row }) => <span className={row.__depth > 0 ? "pla-name-cell pla-name-cell--nested" : "pla-name-cell"} style={{ paddingLeft: row.__depth ? `${row.__depth * 18}px` : undefined }}>{row.name}</span>
         } as Column<PLATableRow>] : []),
         ...(visibleColumns.sortingNumber ? [{
            key: "sortingNumber",
            name: Localization.getText("Sorting number"),
            width: columnWidths.sortingNumber,
            sortable: true,
            resizable: true,
            renderCell: ({ row }) => row.sortingNumber ?? ""
         } as Column<PLATableRow>] : []),
         ...attributeColumns.filter((column) => visibleColumns[column.key] !== false),
         {
            key: "actions",
            name: Localization.getText("Actions"),
            width: props.readOnly ? 54 : 120,
            headerCellClass: "pla-actions-sticky-cell",
            cellClass: "pla-actions-sticky-cell",
            renderCell: ({ row }) => (
               <div className="pla-row-actions">
                  <RowActionButton title={Localization.getText("Open element")} icon="edit" onClick={() => props.onOpenElement(row)} />
                  {!props.readOnly && <RowActionButton title={Localization.getText("Create relation")} icon="mapLink" onClick={() => props.onCreateRelation(row)} />}
                  {!props.readOnly && <RowActionButton title={Localization.getText("Delete element")} icon="delete" onClick={() => props.onDeleteElement(row)} />}
               </div>
            )
         }
      ];
   }, [props.elementType, props.readOnly, props.onOpenElement, props.onCreateRelation, props.onDeleteElement, toggleExpandedElement, visibleColumns, columnWidths]);

   const handleColumnResize = useCallback((idx: number, width: number) => {
      const columnKey = columns[idx]?.key;
      if (!columnKey || columnKey === "expand" || columnKey === "alert" || columnKey === "actions") return;
      setColumnWidths((prev) => ({ ...prev, [columnKey]: width }));
   }, [columns]);

   const columnOptions = useMemo(() => getColumnOptions(props.elementType), [props.elementType]);

   return (
      <div ref={containerRef} className="pla-table" data-testid="pla-table">
         <div className="pla-toolbar">
            <div className="pla-toolbar__row">
               <input
                  className="pla-search"
                  aria-label={Localization.getText("Filter")}
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder={Localization.getText("Search in name, text or attributes...")}
               />
               <details className="pla-columns pla-menu" style={{ position: "relative" }}>
                  <summary className="pla-btn pla-btn--ghost">{Localization.getText("Columns")}</summary>
                  <div className="pla-columns__options">
                     {columnOptions.map((column) => (
                        <label key={column.key}>
                           <input
                              type="checkbox"
                              checked={visibleColumns[column.key] !== false}
                              onChange={(event) => setColumnVisible(column.key, event.target.checked)}
                           />
                           {column.name}
                        </label>
                     ))}
                  </div>
               </details>
               {!props.readOnly && <GlassButton className="pla-btn pla-btn--solid" onClick={props.onAddElement}>{Localization.getText("Add element")}</GlassButton>}
            </div>
         </div>
         <div className="pla-table-wrap">
            <ReactDataGrid
               ref={gridRef}
               columns={columns}
               rows={filteredRows}
               className="rdg-light pla-data-grid"
               sortColumns={sortColumns}
               onSortColumnsChange={setSortColumns}
               onColumnResize={handleColumnResize}
               enableVirtualization={false}
               rowKeyGetter={(row) => row.__rowKey}
               rowClass={(row) => [row.__depth > 0 ? "pla-nested-row" : undefined, row.id === props.selectedElementId ? "rdg-row-selected" : undefined].filter(Boolean).join(" ") || undefined}
               onCellClick={({ row }) => props.onRowClick(row)}
               style={{ flex: "1 1 auto", minHeight: 0, height: "100%", blockSize: "100%" }}
            />
         </div>
      </div>
   );
});
